import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowDown, Train, Users } from "lucide-react";
import { IMAGES, WHATSAPP_URL, scrollToId } from "@/lib/constants";
import { useLanguage } from "@/lib/i18n";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay, revealKey }) {
  return (
    <span className="block overflow-hidden pb-1 -mb-1">
      <motion.span
        key={revealKey}
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const sectionRef = useRef(null);
  const { t, lang } = useLanguage();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const yBack = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yFront = useTransform(scrollYProgress, [0, 1], [0, 70]);

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16"
    >
      <div className="absolute -top-32 -right-40 w-[36rem] h-[36rem] rounded-full bg-terracotta/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -left-48 w-[30rem] h-[30rem] rounded-full bg-cypress/10 blur-3xl pointer-events-none" />

      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            key={`overline-${lang}`}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            className="font-mono-label text-[11px] sm:text-xs uppercase tracking-[0.3em] text-terracotta font-bold mb-6"
            data-testid="hero-overline"
          >
            {t.hero.overline}
          </motion.p>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] text-ink text-balance">
            <MaskedLine delay={0.25} revealKey={`l1-${lang}`}>{t.hero.line1}</MaskedLine>
            <MaskedLine delay={0.4} revealKey={`l2-${lang}`}>
              <em className="text-terracotta">{t.hero.line2}</em>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.65, ease: EASE }}
            className="mt-6 max-w-xl text-sm sm:text-base leading-relaxed text-clay"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8, ease: EASE }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-whatsapp-btn"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 rounded-full bg-whatsapp px-7 py-3.5 text-sm sm:text-base font-semibold text-white transition-all duration-300 hover:brightness-95 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-whatsapp/30"
            >
              <WhatsAppIcon className="w-5 h-5" />
              {t.hero.whatsapp}
            </a>
            <button
              data-testid="hero-discover-btn"
              onClick={() => scrollToId("appartamento")}
              className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-7 py-3.5 text-sm sm:text-base font-semibold text-ink transition-all duration-300 hover:border-terracotta hover:text-terracotta hover:-translate-y-0.5"
            >
              {t.hero.discover}
              <ArrowDown className="w-4 h-4" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-12 pt-6 border-t border-[#E2DCD5] flex flex-wrap gap-x-10 gap-y-4"
            data-testid="hero-stats"
          >
            {t.hero.stats.map(([value, label]) => (
              <div key={label}>
                <p className="font-serif text-2xl sm:text-3xl font-semibold text-ink">{value}</p>
                <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-clay mt-1">{label}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <div
          className="lg:col-span-5 relative h-[420px] sm:h-[520px]"
          style={{ perspective: 1200 }}
          onMouseMove={onMouseMove}
          onMouseLeave={() => {
            mx.set(0);
            my.set(0);
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 40, rotate: 6 }}
            animate={{ opacity: 1, y: 0, rotate: 5 }}
            transition={{ duration: 1.1, delay: 0.55, ease: EASE }}
            style={{ y: yBack }}
            className="absolute top-2 right-0 w-[62%] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl shadow-ink/20 border-4 border-white"
          >
            <img src={IMAGES.esterno} alt="Esterno di Casa Paola ad Arcore" className="w-full h-full object-cover" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 60, rotate: -6 }}
            animate={{ opacity: 1, y: 0, rotate: -4 }}
            transition={{ duration: 1.1, delay: 0.4, ease: EASE }}
            style={{ y: yFront, rotateX, rotateY, transformStyle: "preserve-3d" }}
            className="absolute bottom-4 left-0 w-[72%] aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl shadow-ink/25 border-4 border-white"
          >
            <img src={IMAGES.soggiorno} alt="Soggiorno di Casa Paola con letto singolo" className="w-full h-full object-cover" />
            <div
              style={{ transform: "translateZ(50px)" }}
              className="absolute bottom-4 left-4 rounded-full bg-linen/90 backdrop-blur px-4 py-2 flex items-center gap-2 shadow-lg"
            >
              <Users className="w-3.5 h-3.5 text-terracotta" />
              <span className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-ink font-bold">
                {t.hero.guestsBadge}
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
            className="absolute top-6 left-2 sm:left-6 rounded-full bg-cypress text-linen px-4 py-2 flex items-center gap-2 shadow-xl"
            data-testid="hero-station-badge"
          >
            <Train className="w-3.5 h-3.5" />
            <span className="font-mono-label text-[10px] uppercase tracking-[0.18em] font-bold">
              {t.hero.stationBadge}
            </span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
